// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// Two shells for the Investment Reporting island (mirrors research-hub/ResearchHubLayout,
// which likewise exports its island shell + a sibling):
//
//   * InvestmentReportingLayout — the island shell. Wrapped by a BARE auth-only
//     ProtectedRoute; the team/ops/admin gate is done HERE via useReportingAccess (the
//     server-authoritative can_review_reporting predicate), so client gating is congruent
//     with the RLS. It does NOT use requiredTeam="Investment" (that wrongly excludes Ops).
//
//   * ReportingCycleLayout — the per-cycle shell for the :cycleId child route: cycle header
//     + the report|flags|artifacts|newsletter|gate|intake tab strip + <Outlet/>. The loaded
//     cycle + IR are passed to the tabs via Outlet context.

import { NavLink, Outlet, Navigate, useParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { EmptyState, LoadingState, ErrorState } from "@/components/research-hub";
import { SensitivityTierBadge } from "@/components/investment-reporting";
import { statusLabel, statusTone, toneDot, formatDay } from "@/components/investment-reporting";
import { useReportingAccess } from "@/hooks/investment-reporting/useReportingAccess";
import { useReportingCycle, type ReportingCycleRow } from "@/hooks/investment-reporting/useReportingCycle";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import type { IR } from "@/types/ir";

// Dark-launch flag. The island renders NOTHING until this row exists and is enabled in
// feature_flags — so the surface stays inert until the 0001→0003d DDL is applied.
export const REPORTING_FLAG_KEY = "investment_reporting_enabled";

export const REPORTING_BASE = "/partner/investment-reporting";

// Context the per-cycle tabs read via useOutletContext.
export interface CycleContext {
  cycle: ReportingCycleRow;
  // NOT a write restriction and must never be used as one: since 2026-08-17 admins may drive
  // every write on this island. It survives only so the Gate tab can say what a self-signature
  // will RECORD (four_eyes = 'waived_admin' for an admin, a refusal for anyone else).
  isAdmin: boolean;
  ir: IR | undefined;
  // Gate every reporting_* control on canSign: after migrations 0003r and 0003s it mirrors the
  // predicate EVERY write RPC on this island applies, which is also can_review_reporting().
  canSign: boolean;
  // The last narrower predicate in the estate, and it belongs to the market-context island, not
  // this one: yardeni_curate_and_publish / yardeni_verify_chart_read / yardeni_import_top_findings
  // / yardeni_register_chartbook_edition are still Investment OR Operations only, because neither
  // ruling was about them. YardeniCurate is the only consumer.
  isInvestmentOrOps: boolean;
}

// ============================================================================
// Island shell — access-gated, holds the list + the per-cycle sub-tree.
// ============================================================================
export default function InvestmentReportingLayout() {
  const { enabled: flagOn, isLoading: flagLoading } = useFeatureFlag(REPORTING_FLAG_KEY);
  const access = useReportingAccess();

  // Dark launch: render nothing at all until the flag is explicitly enabled. useFeatureFlag
  // returns enabled=false while loading AND when the row is absent, so the surface is inert
  // by default — no flag row, no island.
  if (flagLoading || !flagOn) return null;

  if (access.loading) {
    return (
      <div className="w-full px-4 py-6">
        <LoadingState rows={3} />
      </div>
    );
  }

  if (!access.canReview) {
    return (
      <div className="w-full px-4 py-10 max-w-2xl mx-auto">
        <EmptyState
          title="You don't have access to investment reporting yet"
          body="This surface is for the Investment and Operations teams. Speak to the investment team if you think you should have access."
        />
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-6 space-y-6 font-body">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold text-primary">Investment reporting</h1>
          <p className="text-muted-foreground text-sm">
            Review the canonical cycle artifact, resolve flags, and sign the IT gate — every number auditable to its source.
          </p>
        </div>
        {/* The badge used to read "Admin, read-only (cannot sign the gate)", then briefly
            "Admin: you may sign the gate. Resolving flags and running the pipeline stay with
            Investment and Operations." NEITHER is true now. Sam's two rulings of 2026-08-17
            (migration 0003s) widened every reporting_* write RPC to admins, including
            resolve_reporting_flag, so an admin on no team has no reporting restriction left to
            state. There is no role badge at all, because there is no role difference left to
            report on this island. The one thing an admin should know is not about access but
            about what a self-signature RECORDS, and the Gate tab says that at the point of the
            click, where it can still change the decision. */}
      </div>
      <Outlet context={{ isAdmin: access.isAdmin }} />
    </div>
  );
}

// ============================================================================
// Per-cycle shell — cycle header + tab strip + <Outlet/> with the CycleContext.
// ============================================================================
const TABS: { seg: string; label: string }[] = [
  { seg: "report", label: "Report" },
  { seg: "flags", label: "Flags" },
  { seg: "artifacts", label: "Artifacts" },
  // The retail-copy review surface. Deliberately its own tab between Artifacts and Gate:
  // reviewing the COPY is a distinct act from signing the NUMBERS, and neither implies the other.
  { seg: "newsletter", label: "Newsletter" },
  { seg: "gate", label: "Gate" },
  { seg: "intake", label: "Intake" },
  // The active/passive sleeve roster (0003v). Its own tab because confirming a sleeve is the
  // ONLY act on this island that cannot be performed from a server session at all —
  // confirm_sleeve() refuses a NULL auth.uid(), so without a signed-in human clicking here the
  // fourteen sleeves stay unconfirmed and 45 active_contribution cells publish not_found.
  { seg: "sleeves", label: "Sleeves" },
  // The Yardeni second-opinion route (partnerOpsRoutes.tsx:554) was mounted but never given a tab,
  // so it was reachable only by typing the URL. This is the one-line follow-up ROUTES.md:90 always
  // specified; both of its stated preconditions now hold — the review shell is merged, and the
  // 0003d objects are applied (get_current_market_context + yardeni_synthesis_runs.market_context
  // exist live, and the RPC returns a well-formed "no published slot" envelope that the page
  // renders as an honest EmptyState rather than erroring).
  //
  // No extra gating here, deliberately: this strip renders INSIDE InvestmentReportingLayout, which
  // already gates on the investment_reporting_enabled flag AND can_review_reporting (Investment ∪
  // Operations ∪ admins). A tab is therefore unreachable unless its route is. Do NOT add
  // requiredTeam="Investment" — that would exclude Operations and diverge from the RLS predicate.
  { seg: "yardeni", label: "Yardeni" },
];

export function ReportingCycleLayout() {
  const { cycleId } = useParams<{ cycleId: string }>();
  const access = useReportingAccess();
  const { data, isLoading, error, refetch } = useReportingCycle(cycleId);

  // CioToday ordering: loading, then an UNCONDITIONAL error, then empty.
  if (isLoading && !data)
    return (
      <div className="space-y-4">
        <LoadingState rows={2} />
      </div>
    );
  if (error) return <ErrorState onRetry={() => refetch()} />;

  const cycle: ReportingCycleRow | null = data ?? null;

  if (!cycle) return <EmptyState title="That reporting cycle could not be found." />;

  const ir = cycle.ir ?? undefined;
  const base = `${REPORTING_BASE}/${cycleId}`;
  const tone = statusTone(cycle.status);

  return (
    <div className="space-y-5">
      {/* Cycle header */}
      <div className="flex items-start justify-between gap-4 flex-wrap border-b pb-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-heading text-xl font-semibold text-foreground">{cycle.period_label}</h2>
            <span className="inline-flex items-center gap-1.5 font-body text-xs">
              <span className={cn("h-1.5 w-1.5 rounded-full", toneDot(tone))} aria-hidden />
              {statusLabel(cycle.status)}
            </span>
            <span className="font-body text-[11px] rounded px-1.5 py-0.5 bg-muted text-muted-foreground">
              stage: {cycle.current_stage}
            </span>
          </div>
          <p className="font-body text-xs text-muted-foreground">
            As of {formatDay(cycle.as_of_date)} · {cycle.period_type}
            {cycle.is_webinar_month ? " · webinar month" : ""}
          </p>
        </div>
        <NavLink to={REPORTING_BASE} className="font-body text-sm text-primary hover:underline shrink-0">
          ← All cycles
        </NavLink>
      </div>

      {/* Tab strip */}
      <nav className="flex gap-1 flex-wrap border-b pb-2">
        {TABS.map((t) => (
          <NavLink
            key={t.seg}
            to={`${base}/${t.seg}`}
            className={({ isActive }) =>
              cn(
                "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )
            }
          >
            {t.label}
          </NavLink>
        ))}
      </nav>

      <Outlet
        context={
          {
            cycle,
            ir,
            isAdmin: access.isAdmin,
            canSign: access.canSign,
            isInvestmentOrOps: access.isInvestmentOrOps,
          } satisfies CycleContext
        }
      />
    </div>
  );
}

// Default child redirect: :cycleId -> :cycleId/report
export function ReportingCycleIndex() {
  const { cycleId } = useParams<{ cycleId: string }>();
  return <Navigate to={`${REPORTING_BASE}/${cycleId}/report`} replace />;
}
